// @flow

import * as React from 'react'
import { Segment, Button, Dimmer, Loader } from 'semantic-ui-react'
import Dropzone from 'react-dropzone'

import Worker from './xlsx-parser.worker.js'

type CheckRecordsCallbackResult = {|
  data: Array<Object>,
  valid: boolean,
  message: string,
|}
export type CheckRecordsCallback = (Array<Object>) => Promise<CheckRecordsCallbackResult> | CheckRecordsCallbackResult
type Props = {|
  onError: (msg: string) => void,
  onAbort: (msg: string) => void,
  onStartParse: (filename: string) => void,
  onEndParse: (cols: Array<string>, rows: Array<Object>, message: string) => void,
  columns: Array<string>,
  strictColumnsCheck: boolean,
  updateStatus?: boolean,
  checkRecords: boolean,
  checkRecordsCallback: CheckRecordsCallback,
|}

type State = {|
  dimmed: boolean,
  worker: any,
|}

class XLSXParser extends React.Component<Props, State> {
  static defaultProps = {
    onError: () => {},
    onAbort: () => {},
    onStartParse: () => {},
    onEndParse: () => {},
    columns: [],
    strictColumnsCheck: false,
    updateStatus: false,
    checkRecords: false,
    checkRecordsCallback: (data: Array<Object>) => {
      return { data, valid: true, message: 'OK' }
    },
  }

  constructor(props: Props) {
    super(props)
    this.state = { dimmed: false, worker: undefined }
  }

  componentWillUnmount() {
    if (this.state.worker) {
      this.state.worker.terminate()
    }
  }

  onDrop = (acceptedFiles: Array<File>, rejectedFiles: Array<File>) => {
    if (rejectedFiles.length > 0) {
      this.props.onError('Il file selezionato non è nel formato consentito')
    } else if (acceptedFiles.length > 0) {
      let file = acceptedFiles[0]
      if (/xlsx$/.test(file.name)) {
        this.props.onStartParse(file.name)

        if (typeof Worker !== 'undefined') {
          this.handleFileAsync(file)
        } else {
          this.handleFileSync(file)
        }
      } else {
        this.props.onError('Il file selezionato non è nel formato consentito')
      }
    }
  }

  onParse = async (cols: Array<string>, data: Array<Object>) => {
    // Trim column headers
    cols = cols.map(c => {
      return c.trim()
    })

    // Check null columns
    let nullHeader = cols.indexOf('')
    if (nullHeader !== -1) {
      this.setState({ dimmed: false })
      return this.props.onError('Nome colonna n. ' + (nullHeader + 1) + ' non valido')
    }

    // Check duplicate columns
    let duplicates = cols.reduce((acc, el, i, arr) => {
      if (arr.indexOf(el) !== i && acc.indexOf(el) < 0) acc.push(el)
      return acc
    }, [])
    if (duplicates.length > 0) {
      this.setState({ dimmed: false })
      return this.props.onError('Nomi colonne duplicati (' + duplicates.join(', ') + ')')
    }

    // Check columns match to configs
    if (this.props.updateStatus) {
      if (cols.length !== 2 || cols[0] !== 'id' || ['stato_f2f', 'stato_phone', 'stato_web'].indexOf(cols[1]) === -1) {
        this.setState({ dimmed: false })
        return this.props.onError('Colonne caricate non corrispondenti al tracciato richiesto')
      }
      let checkResult = await this.props.checkRecordsCallback(data)
      if (checkResult.valid) {
        this.props.onEndParse(cols, checkResult.data, checkResult.message)
      } else {
        this.props.onError(checkResult.message)
      }
    } else {
      if (this.props.columns) {
        if (this.props.strictColumnsCheck && cols.length !== this.props.columns.length) {
          this.setState({ dimmed: false })
          return this.props.onError('Colonne caricate non corrispondenti al tracciato richiesto')
        }

        let colsMatch = this.props.columns.reduce((match, key, idx) => {
          return match && key === cols[idx]
        }, true)

        if (colsMatch) {
          if (this.props.checkRecords) {
            let checkResult = await this.props.checkRecordsCallback(data)
            if (checkResult.valid) {
              this.props.onEndParse(cols, checkResult.data, checkResult.message)
            } else {
              this.props.onError(checkResult.message)
            }
          } else {
            this.props.onEndParse(cols, data, 'OK')
          }
        } else {
          this.props.onError('Colonne caricate non corrispondenti al tracciato richiesto')
        }
      }
    }
    this.setState({ dimmed: false })
  }

  handleFileAsync = (file: File) => {
    // $FlowFixMe
    let worker = new Worker()
    this.setState({ worker })

    worker.onmessage = event => {
      this.setState({ worker: undefined })
      switch (event.data.state) {
        case 'ERROR':
          this.setState({ dimmed: false })
          this.props.onError(event.data.msg)
          break

        case 'ABORT':
          this.setState({ dimmed: false })
          this.props.onAbort(event.data.msg)
          break

        case 'SUCCESS':
          this.onParse(event.data.cols, event.data.rows)
          break

        default:
          break
      }
      worker.terminate()
    }

    this.setState({ dimmed: true })
    worker.postMessage(file)
  }

  handleFileSync = (file: File) => {
    const reader = new FileReader()

    reader.onload = e => {
      import('xlsx').then(XLSX => {
        const bstr = new Uint8Array(e.target.result)
        const wb = XLSX.read(bstr, { type: 'array' })
        const wsname = wb.SheetNames[0]
        const ws = wb.Sheets[wsname]
        let data = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' }).filter(r => {
          return r.join('').length > 0
        })
        let cols = data.shift()
        this.setState({ dimmed: false })
        this.onParse(cols, data)
      })
    }
    reader.onabort = () => {
      this.setState({ dimmed: false })
      this.props.onAbort('Caricamento file interrotto')
    }
    reader.onerror = () => {
      this.setState({ dimmed: false })
      this.props.onError('Errore nel caricamento file')
    }

    this.setState({ dimmed: true })
    setTimeout(() => {
      //WORKAROUND TO ENSURE DIMMER WORKS
      // $FlowFixMe
      reader.readAsArrayBuffer(file)
    }, 150)
  }

  render() {
    return (
      <Dimmer.Dimmable dimmed={this.state.dimmed}>
        <Dimmer active={this.state.dimmed} inverted style={{ zIndex: '700' }}>
          <Loader>Analisi file in corso...</Loader>
        </Dimmer>
        <Dropzone multiple={false} onDrop={this.onDrop} style={{ position: 'relative', display: 'block' }}>
          <Segment basic style={{ border: 'dashed 3px #c8c8c8', borderRadius: '10px' }}>
            <div style={{ textAlign: 'center', padding: '1ex 0' }}>
              <Button circular size="huge" style={{ color: 'white', width: '95%' }}>
                Importa file<br />
                <small>
                  Trascina un file in questa zona <br />o clicca per selezionarlo
                </small>
              </Button>
            </div>
          </Segment>
        </Dropzone>
      </Dimmer.Dimmable>
    )
  }
}

export default XLSXParser



// WEBPACK FOOTER //
// ./src/systemwide/xlsx-parser.js